/**
 * Visual Translation Editor — Entry Point
 * Self-executing IIFE bundle loaded by edge proxy or auto-init when ?__lr_editor is detected.
 *
 * Reads script tag attributes, polls for window.LionRapid, creates Shadow DOM,
 * and instantiates Inspector, Sidebar, and Toolbar.
 *
 * Auth flow: grant token → exchange for scoped session JWT → use JWT for API calls.
 */

import { EDITOR_CSS } from './editor-styles.js';
import { DEFAULT_API_BASE_URL } from '../../config/api.config.js';
import { Inspector } from './inspector.js';
import { Toolbar } from './toolbar.js';
import { Sidebar } from './sidebar.js';
import { EditorAPI } from './editor-api.js';
import type { ResolvedKey } from './key-resolver.js';
import { captureSnapshot } from './snapshot-capture.js';
import { activateSnapshotViewer } from './snapshot-viewer.js';
import { BlockExtractor } from '../block-extractor.js';
import { localizeUrl } from './locale-url.js';

// Console diagnostic. Attached at module load time so it's available the
// instant the editor bundle parses — before any grant exchange, before any
// async setup. Sidebar overrides this later with a richer version that also
// reports each block's translation status.
//
// Usage in DevTools:  __lr_dump_keys()
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__lr_dump_keys = () => {
    const extractor = new BlockExtractor();
    const blocks = extractor.extract(document.body, true);
    const rows = blocks.map((b, i) => ({
      i,
      key: b.key,
      text: b.unit.text,
      stylesJson: JSON.stringify(b.unit.styles),
    }));
    console.table(rows);
    return rows;
  };
}

// ============================================
// Session helpers
// ============================================

const SESSION_KEY = '__lr_editor_session';

interface StoredSession {
  token: string;
  exp: number;
}

/**
 * Session-scoped cookie the edge proxy reads to decide whether to fetch
 * draft translations (bypassing the delivery gate). Set in lockstep with
 * the sessionStorage JWT so a live editor session always implies preview.
 *
 * Not sensitive on its own — it carries no auth material, just a signal.
 * The actual auth is the JWT sent via Authorization headers from this JS.
 */
const PREVIEW_COOKIE = '__lr_editor_active';

function setPreviewCookie(): void {
  // No Max-Age / Expires → session cookie, clears when the browser/tab
  // closes. Matches the lifetime of sessionStorage so both signals go
  // away together.
  document.cookie = `${PREVIEW_COOKIE}=1; path=/; SameSite=Lax`;
}

function clearPreviewCookie(): void {
  document.cookie = `${PREVIEW_COOKIE}=; path=/; SameSite=Lax; Max-Age=0`;
}

function getStoredSession(): string | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed: StoredSession = JSON.parse(raw);
    // Check if expired (with 30s buffer)
    if (parsed.exp * 1000 < Date.now() + 30_000) {
      sessionStorage.removeItem(SESSION_KEY);
      clearPreviewCookie();
      return null;
    }
    // Re-stamp the preview cookie on every load — covers the case where
    // the user closed and reopened the tab inside the sessionStorage TTL,
    // but the session cookie expired with the previous tab.
    setPreviewCookie();
    return parsed.token;
  } catch {
    return null;
  }
}

function storeSession(token: string): void {
  try {
    // Parse exp from JWT payload (es_<header>.<payload>.<sig>)
    const raw = token.startsWith('es_') ? token.slice(3) : token;
    const parts = raw.split('.');
    if (parts.length !== 3) return;
    const payload = JSON.parse(atob(parts[1]));
    const session: StoredSession = { token, exp: payload.exp || 0 };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    setPreviewCookie();
  } catch {
    // Non-critical — session just won't persist across navigations
  }
}

function clearSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
  clearPreviewCookie();
}

/**
 * Decode the role claim from a signed editor session JWT.
 * Used for client-side UI gating (show/hide Capture button etc.) — the
 * server enforces the same check on every protected endpoint.
 */
function extractSessionRole(token: string): string {
  try {
    const raw = token.startsWith('es_') ? token.slice(3) : token;
    const parts = raw.split('.');
    if (parts.length !== 3) return '';
    const payload = JSON.parse(atob(parts[1]));
    return typeof payload.role === 'string' ? payload.role : '';
  } catch {
    return '';
  }
}

// ============================================
// User-visible notice (shown when editor can't activate)
// ============================================

function showEditorNotice(message: string): void {
  const el = document.createElement('div');
  el.setAttribute('data-no-translate', '');
  Object.assign(el.style, {
    position: 'fixed', bottom: '24px', right: '24px', zIndex: '2147483647',
    background: '#1a1a2e', color: '#fff', padding: '16px 20px',
    borderRadius: '8px', fontFamily: 'system-ui, sans-serif', fontSize: '14px',
    maxWidth: '360px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  });
  el.textContent = message;
  const close = document.createElement('button');
  Object.assign(close.style, {
    position: 'absolute', top: '4px', right: '8px',
    background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '16px',
  });
  close.textContent = '\u00d7';
  close.addEventListener('click', () => el.remove());
  el.appendChild(close);
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 8000);
}

// ============================================
// Init
// ============================================

function initEditor(): void {
  // Support two activation modes:
  //   1. Grant mode — script tag has data-grant (first-page activation)
  //   2. Session mode — no grant, but a valid session exists in sessionStorage
  //      (subsequent page navigation after grant was consumed)
  //
  // The edge proxy injects the editor script on every page when editor.enabled
  // is true. On pages where there's no active session, the editor silently
  // exits — no FAB, no network calls.
  const scriptEl =
    (document.querySelector('script[data-grant]') as HTMLScriptElement | null) ||
    (document.querySelector('script[data-lr-editor]') as HTMLScriptElement | null);

  if (!scriptEl) {
    // No editor script tag at all — nothing to do
    return;
  }

  const grant = scriptEl.dataset.grant || '';
  const hasValidSession = getStoredSession() !== null;

  // Session mode requires an existing stored session; grant mode requires a grant
  if (!grant && !hasValidSession) {
    // No grant and no session — silently exit (normal visitor, not an editor user)
    return;
  }

  const locale = scriptEl.dataset.locale || '';
  const apiBase = scriptEl.dataset.api || DEFAULT_API_BASE_URL;
  const projectId = scriptEl.dataset.project || '';

  // Poll for window.LionRapid (auto-init loads first)
  let attempts = 0;
  const maxAttempts = 100; // 10s at 100ms intervals

  const poll = setInterval(() => {
    attempts++;
    const lr = window.LionRapid;

    // Duck-type: during auto-init's boot window (or under a WP/edge library
    // bundle) window.LionRapid can hold a module-exports namespace, not the
    // instance. Only the instance has getCurrentLocale.
    if (lr && typeof lr.getCurrentLocale === 'function') {
      clearInterval(poll);
      activate(lr, grant, locale, apiBase, projectId);
    } else if (attempts >= maxAttempts) {
      clearInterval(poll);
      console.warn('[LionRapid Editor] Timed out waiting for window.LionRapid');
    }
  }, 100);
}

async function activate(
  lr: NonNullable<typeof window.LionRapid>,
  grant: string,
  locale: string,
  apiBase: string,
  projectId: string,
): Promise<void> {
  const config = window.__lionrapidConfig;
  const languages = config?.languages || [locale];
  const namespace = config?.namespace || 'default';
  const defaultLocale = config?.defaultLocale || 'en';
  const currentLocale = locale || lr.getCurrentLocale();
  const normalizedApiBase = apiBase.replace(/\/$/, '');

  // --- Session acquisition ---
  let sessionToken = getStoredSession();

  if (!sessionToken && grant) {
    // Exchange grant for session JWT
    try {
      const res = await fetch(`${normalizedApiBase}/api/editor/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grant, domain: window.location.host }),
      });
      if (!res.ok) {
        console.error('[LionRapid Editor] Grant exchange failed:', res.status);
        showEditorNotice('Editor session expired. Please reopen the editor from the dashboard.');
        return;
      }
      const data = await res.json();
      sessionToken = data.session_token;
      if (sessionToken) {
        storeSession(sessionToken);
      }
    } catch (err) {
      console.error('[LionRapid Editor] Grant exchange error:', err);
      showEditorNotice('Could not connect to LionRapid. Please check your connection and try again.');
      return;
    }
  }

  if (!sessionToken) {
    console.error('[LionRapid Editor] No valid session. Please reopen the editor from the dashboard.');
    showEditorNotice('Editor session expired. Please reopen the editor from the dashboard.');
    return;
  }

  // Decode the session JWT to extract the user's role. The server signs this
  // token and enforces permissions — the client-side check here is only to
  // avoid showing actions the server would reject (defense in depth). Never
  // trust client-side role checks for security decisions.
  const role = extractSessionRole(sessionToken);
  const canCaptureSnapshots = role === 'project_manager' || role === 'admin';

  // Create Shadow DOM host
  const host = document.createElement('div');
  host.id = '__lionrapid-editor';
  host.setAttribute('data-no-translate', '');
  document.body.appendChild(host);
  const shadow = host.attachShadow({ mode: 'closed' });

  // Inject styles
  const styleEl = document.createElement('style');
  styleEl.textContent = EDITOR_CSS;
  shadow.appendChild(styleEl);

  // Hide the proxy/auto-init language switcher while editor is active
  const switcherEl = document.querySelector('[data-lionrapid-switcher]') as HTMLElement | null;
  if (switcherEl) switcherEl.style.display = 'none';

  // Create API client with session token
  const api = new EditorAPI(normalizedApiBase, sessionToken, projectId);
  api.startAutoFlush();

  // --- Snapshot viewer mode ---
  // If the URL has ?__lr_snapshot={uuid}, replace the page with the saved
  // snapshot HTML and scope the Inspector to the sandbox wrapper. Capture
  // button is hidden in viewer mode (a translator shouldn't re-capture).
  //
  // Separately, ?__lr_recapture={uuid} runs on the LIVE page (no viewer)
  // but switches the FAB's Capture button into re-capture mode — PUT to the
  // existing snapshot uuid instead of POST.
  const currentUrl = new URL(window.location.href);
  const snapshotUuid = currentUrl.searchParams.get('__lr_snapshot');
  const recaptureUuid = currentUrl.searchParams.get('__lr_recapture');
  let inspectorRoot: Element | undefined;
  let snapshotKeyMap: Map<number, string> | undefined;
  if (snapshotUuid) {
    const result = await activateSnapshotViewer(snapshotUuid, api);
    if (!result) {
      showEditorNotice('Snapshot not found or expired. Please check the dashboard.');
      return;
    }
    inspectorRoot = result.root;
    snapshotKeyMap = result.keyMap;
  }

  // Create sidebar (needs api + locale)
  const handleBlockSelect = (resolved: ResolvedKey) => {
    sidebar.open(resolved);
    inspector.clearHighlight();
  };

  const sidebar = new Sidebar(shadow, api, currentLocale, namespace, defaultLocale, handleBlockSelect, role);

  // Create inspector (hover/click → sidebar)
  const inspector = new Inspector(shadow, handleBlockSelect, inspectorRoot, snapshotKeyMap);

  // Create toolbar
  const toolbar = new Toolbar(
    shadow,
    languages,
    currentLocale,
    // Language change
    async (newLocale) => {
      // Edge proxy context: navigate to locale URL instead of client-side translation
      const isEdgeProxy = document.querySelector('script[data-mode="dynamic-only"]') !== null;
      if (isEdgeProxy) {
        const url = new URL(window.location.href);
        // Strip existing locale prefix if present
        const stripped = url.pathname.replace(/^\/[a-z]{2}(\/|$)/, '/');
        // Build new URL: /{locale}/path (no locale prefix for the default).
        const prefix = newLocale === defaultLocale ? '' : `/${newLocale}`;
        url.pathname = prefix + (stripped === '/' && prefix ? '' : stripped);
        // Don't re-append ?__lr_editor — by now the session is established and
        // carried by the __lr_editor_active cookie (the server re-injects the
        // editor on every page from it). The grant is single-use and already
        // consumed; appending an empty one only confused the server's editor
        // detection and dropped the FAB after a language switch.
        window.location.href = url.toString();
        return;
      }
      sidebar.updateLocale(newLocale);
      sidebar.close();
      inspector.clearHighlight();
      await lr.changeLanguage(newLocale);
      await sidebar.hydrateStatus();
    },
    // Exit
    async () => {
      await api.stopAndFlush();
      inspector.destroy();
      toolbar.destroy();
      sidebar.destroy();
      host.remove();
      clearSession();
    },
    // Capture Snapshot — only available in live-page mode (not viewer) AND
    // only for PM/admin roles. Translators and proofreaders cannot capture;
    // the server enforces the same check on POST /api/editor/snapshots.
    //
    // If ?__lr_recapture={uuid} is present, the same button re-captures that
    // existing snapshot (PUT) instead of creating a new one (POST).
    snapshotUuid || !canCaptureSnapshots
      ? null
      : async () => {
          try {
            const snapshot = captureSnapshot();
            if (recaptureUuid) {
              const result = await api.recaptureSnapshot(recaptureUuid, snapshot);
              if (result) {
                showEditorNotice(
                  `Re-captured as v${result.version} (${snapshot.textEntries.length} blocks). You can close this tab.`,
                );
              } else {
                showEditorNotice('Re-capture failed. Please try again.');
              }
            } else {
              const result = await api.createSnapshot(snapshot);
              if (result) {
                showEditorNotice(
                  `Snapshot captured (${snapshot.textEntries.length} blocks). Open it from the Snapshots tab in the dashboard.`,
                );
              } else {
                showEditorNotice('Snapshot upload failed. Please try again.');
              }
            }
          } catch (err) {
            console.error('[LionRapid Editor] Snapshot capture error:', err);
            showEditorNotice('Snapshot capture failed. See console for details.');
          }
        },
    // When in recapture mode, surface it to the toolbar so the button label
    // can change from "Capture" to "Save re-capture".
    recaptureUuid ? { mode: 'recapture' } : undefined,
    // Browse-mode toggle. Suspending the inspector lets the PM click the
    // site's own links to reach another page; the session rides along via the
    // sessionStorage JWT + __lr_editor_active cookie, so the editor re-activates
    // on the destination. Disabled in snapshot viewer mode (static sandbox,
    // nothing to navigate to).
    snapshotUuid
      ? null
      : (browsing: boolean) => {
          inspector.setActive(!browsing);
          if (browsing) sidebar.close();
        },
  );

  // Wire progress counter to sidebar status
  sidebar.setProgressCallback((translated, total) => {
    toolbar.updateProgress(translated, total);
  });

  // Locale-preserving link interceptor. On /es/, internal links like
  // /about/ would naively navigate to the default-language page; this
  // rewrites them to /es/about/ so the operator stays in the locale they
  // were browsing. Runs in BUBBLE phase so the inspector (capture phase)
  // gets first say — if the inspector preventDefault'd a block click, this
  // never fires. New-tab modifiers and editor-UI clicks are left alone.
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if ((e as MouseEvent).button !== 0) return;
    const target = e.target as Element | null;
    if (!target?.closest) return;
    if (target.closest('#__lionrapid-editor')) return;
    const a = target.closest('a[href]') as HTMLAnchorElement | null;
    if (!a) return;
    if (a.target && a.target !== '_self') return;
    if (a.hasAttribute('download')) return;
    const localized = localizeUrl(a.href, defaultLocale);
    if (localized !== a.href) {
      e.preventDefault();
      window.location.href = localized;
    }
  });

  // Hydrate status from server and update progress
  sidebar.hydrateStatus().then(() => {
    toolbar.updateProgress(sidebar.getTranslatedCount(), sidebar.getTotalCount());
  });

  // Clean editor params from URL
  const url = new URL(window.location.href);
  let dirty = false;
  if (url.searchParams.has('__lr_editor')) {
    url.searchParams.delete('__lr_editor');
    dirty = true;
  }
  if (url.searchParams.has('__lr_snapshot')) {
    url.searchParams.delete('__lr_snapshot');
    dirty = true;
  }
  if (url.searchParams.has('__lr_recapture')) {
    url.searchParams.delete('__lr_recapture');
    dirty = true;
  }
  if (dirty) {
    history.replaceState(null, '', url.toString());
  }
}

// Self-execute
initEditor();
